import React, { useEffect, useState } from "react";
import { authClient } from "@lib/okta";
import { Loader } from "@components/Loader";
import {
  PREVIEW_HANDOFF_MESSAGE_TYPE,
  readPreviewHandoffNonce,
} from "@utils/previewLoginHandoff";

/**
 * Rendered at `/login/callback` ONLY when a preview popup handoff is in progress (App.tsx
 * detects this from the Okta `state` param; normal logins render <LoginCallback> instead).
 * Completes the Okta token exchange in this popup, posts the tokens back to the editor iframe
 * (the opener), and closes. Token exchange happens here — in the popup's first-party storage —
 * so the partitioned iframe storage never enters the PKCE handshake.
 */
export const PreviewLoginRelay: React.FC = () => {
  const [message, setMessage] = useState("Completing sign-in…");

  useEffect(() => {
    void (async () => {
      // Read the nonce before parseFromUrl, which may strip the query from the URL.
      const nonce = readPreviewHandoffNonce();
      try {
        const { tokens } = await authClient.token.parseFromUrl();
        const opener = window.opener as Window | null;
        if (opener && !opener.closed) {
          opener.postMessage(
            { type: PREVIEW_HANDOFF_MESSAGE_TYPE, nonce, tokens },
            window.location.origin, // exact target origin (popup origin === opener origin)
          );
          window.close();
          return;
        }
        setMessage("Signed in. You can close this window and return to the editor.");
      } catch {
        setMessage("Sign-in failed. Please close this window and try again.");
      }
    })();
  }, []);

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3">
      <Loader />
      <p className="text-sm text-gray-600">{message}</p>
    </div>
  );
};
